// ── Cash-flow oszlopdiagram (12 hónap, árbevétel vs költség) ───────────────
// Bemenet: [{ month: "2025-08", income_huf: 0, expense_huf: 0 }, …]
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { huFmt } from '../../utils';
import { MoneyTooltip, axisTick, gridProps, legendProps, barCursor } from './ChartFrame';
import { useEnterAnimation } from '../../hooks/useMotion';

const HU_MONTHS_SHORT = ['jan', 'febr', 'márc', 'ápr', 'máj', 'jún',
                         'júl', 'aug', 'szept', 'okt', 'nov', 'dec'];
const HU_MONTHS_LONG  = ['január', 'február', 'március', 'április', 'május', 'június',
                         'július', 'augusztus', 'szeptember', 'október', 'november', 'december'];

const monthIdx   = (m) => parseInt(String(m).slice(5, 7), 10) - 1;
const shortMonth = (m) => HU_MONTHS_SHORT[monthIdx(m)] ?? m;
const longMonth  = (m) => {
  const i = monthIdx(m);
  return HU_MONTHS_LONG[i] ? `${String(m).slice(0, 4)}. ${HU_MONTHS_LONG[i]}` : m;
};

// Nettó sor a tooltip aljára: árbevétel − költség, előjeles, színezett.
function netFooter(payload) {
  const income  = payload.find(p => p.dataKey === 'income_huf')?.value ?? 0;
  const expense = payload.find(p => p.dataKey === 'expense_huf')?.value ?? 0;
  const net = income - expense;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Nettó</span>
      <span style={{
        marginLeft: 'auto', fontFamily: 'var(--mono)', fontWeight: 600,
        fontVariantNumeric: 'tabular-nums', paddingLeft: 12,
        color: net >= 0 ? 'var(--success)' : 'var(--danger)',
      }}>
        {net > 0 ? '+' : ''}{huFmt(net)} Ft
      </span>
    </div>
  );
}

export default function CashflowChart({ data = [], height = 240 }) {
  // ⚠ Belépő animáció CSAK mountkor. A Recharts alapból minden adatváltozásra
  // újraanimál — 60 mp-es staleTime mellett a chart percenként újrarajzolódna a
  // felhasználó szeme előtt. A hook a mount után kikapcsolja magát.
  const animate = useEnterAnimation(600);
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} barGap={2} barCategoryGap="26%"
        margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid {...gridProps} />
        <XAxis dataKey="month" tickFormatter={shortMonth} tick={axisTick}
          axisLine={{ stroke: 'var(--border)' }} tickLine={false} interval={0} />
        <YAxis tickFormatter={(v) => `${huFmt(Math.round(v / 1e6))} M`} tick={axisTick}
          axisLine={false} tickLine={false} width={46} />
        <Tooltip cursor={barCursor}
          content={<MoneyTooltip labelFormatter={longMonth} footer={netFooter} />} />
        <Legend {...legendProps} />
        <Bar dataKey="income_huf" name="Árbevétel" fill="var(--chart-in)"
          radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={animate} />
        <Bar dataKey="expense_huf" name="Költség" fill="var(--chart-out)"
          radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={animate} />
      </BarChart>
    </ResponsiveContainer>
  );
}
